import { motion } from "framer-motion";
import { useState } from "react";

/* ------------------------------------------------------------------
   BrandsMarquee — infinite horizontal scroll of brand wordmarks/logos
   that Mad House Studios has worked with.

   Built as a CSS-keyframe marquee for battery-friendly animation.
   Each logo uses Clearbit's Logo API with a Google-favicon fallback
   (the same recipe AwardLogoStrip already uses), so we don't need
   to commit any image assets.
   ------------------------------------------------------------------ */

const BRANDS = [
    { id: "nike", name: "Nike", domain: "nike.com" },
    { id: "crocs", name: "Crocs", domain: "crocs.com" },
    { id: "cred", name: "CRED", domain: "cred.club" },
    { id: "himalaya", name: "Himalaya", domain: "himalayawellness.in" },
    { id: "bgmi", name: "BGMI", domain: "battlegroundsmobileindia.com" },
    { id: "royal-enfield", name: "Royal Enfield", domain: "royalenfield.com" },
    { id: "kfc", name: "KFC", domain: "kfc.com" },
    { id: "duolingo", name: "Duolingo", domain: "duolingo.com" },
    { id: "solana", name: "Solana", domain: "solana.com" },
    { id: "agewell", name: "Agewell", domain: "agewellfoundation.org" },
    { id: "treet", name: "Treet", domain: "treet.co" },
    { id: "samsung", name: "Samsung", domain: "samsung.com" },
    { id: "adidas", name: "Adidas", domain: "adidas.com" },
    { id: "apple", name: "Apple", domain: "apple.com" },
    { id: "spotify", name: "Spotify", domain: "spotify.com" },
    { id: "netflix", name: "Netflix", domain: "netflix.com" },
    { id: "mercedes", name: "Mercedes-Benz", domain: "mercedes-benz.com" },
    { id: "amazon", name: "Amazon", domain: "amazon.com" }
];

const LogoImg = ({ domain, alt }) => {
    // Try Clearbit first (clean transparent SVG), fallback to Google favicon
    // (64px, always works but slightly lower-fi).
    const [src, setSrc] = useState(`https://logo.clearbit.com/${domain}?size=128&format=png`);
    const onError = () => {
        const fallback = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
        if (src !== fallback) setSrc(fallback);
    };
    return (
        <img
            src={src}
            onError={onError}
            alt={alt}
            draggable={false}
            loading="lazy"
            className="h-8 w-auto select-none object-contain opacity-80 grayscale transition-[filter,opacity] duration-500 group-hover/brand:opacity-100 group-hover/brand:grayscale-0 md:h-10"
            style={{ filter: "drop-shadow(0 0 14px rgba(255,255,255,0.04))" }}
        />
    );
};

const BrandCell = ({ brand }) => (
    <span
        data-testid={`brand-logo-${brand.id}`}
        className="group/brand relative mx-10 inline-flex items-center gap-3 md:mx-14"
        aria-label={brand.name}
    >
        <LogoImg domain={brand.domain} alt={brand.name} />
        <span className="font-display text-sm uppercase tracking-[0.22em] text-fog-secondary/80 transition-colors duration-500 group-hover/brand:text-fog-primary md:text-base">
            {brand.name}
        </span>
    </span>
);

export const BrandsMarquee = () => {
    // Triple the list so the existing `.mh-marquee` keyframe
    // (which translates to -33.3333%) seamlessly loops — two copies
    // wouldn't line up with the existing global keyframe.
    const duplicated = [...BRANDS, ...BRANDS, ...BRANDS];

    return (
        <section
            id="brands"
            data-testid="brands-marquee"
            className="relative overflow-hidden border-y border-ink-border/50 bg-ink py-14 md:py-20"
        >
            {/* Eyebrow */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.6 }}
                className="mb-10 flex items-center justify-center gap-3 font-mono text-[10px] uppercase tracking-[0.45em] text-fog-muted md:mb-14 md:text-xs"
            >
                <span className="inline-block h-px w-10 bg-fog-muted/60" />
                Brands we've worked with
                <span className="inline-block h-px w-10 bg-fog-muted/60" />
            </motion.div>

            {/* Edge fades so logos enter/exit smoothly instead of hard-cutting */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-ink to-transparent md:w-40"
            />
            <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-ink to-transparent md:w-40"
            />

            {/* Marquee rail — slow brands variant of the global marquee
                keyframe (translates to -33.3333% → triple the list for a
                seamless loop). Paused on hover. */}
            <div className="mh-brands-marquee">
                {duplicated.map((brand, i) => {
                    const copy =
                        i < BRANDS.length ? "a" : i < BRANDS.length * 2 ? "b" : "c";
                    return (
                        <BrandCell
                            key={`${brand.id}-${copy}`}
                            brand={brand}
                        />
                    );
                })}
            </div>
        </section>
    );
};

export default BrandsMarquee;
